import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { HeadTag } from 'shared/components';
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import ClassIcon from '@mui/icons-material/Class';
import {
  AppFormikButtonTiles,
  AppFormikSelect,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import { Formik, useFormikContext } from 'formik';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AISystemsService } from 'services/AISystemService';
import { IAISystem } from '../components/AISystemRecorder';
import { TechnicalFields } from '../form-source-type/Main';
import DateFnsAdapter from '@date-io/date-fns';
import LinkedDatasets from './components/LinkedDatasets';
import LinkedAssessments from './components/LinkedAssessments';
import { ReactComponent as Edit } from 'assets/images/ic_edit.svg';
import { ReactComponent as Trash } from 'assets/images/ic_trash.svg';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { ReactComponent as Back } from 'assets/images/ic_back.svg';
import { ReactComponent as ApprovedCheck } from 'assets/images/approved-check.svg';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import { Toast, Utils } from 'shared/helpers';
import { EntityType } from 'shared/enums';
import LinkedRiskRegisters from './components/LinkedRiskRegisters';
import QuickRunAssessment from './components/QuickRunAssessment';
import { BROADCAST_MESSAGES } from 'shared/Constants';

import Documents from './components/Documents';
import Complience from './components/Complience';
import { TierApproverService } from 'services/TierApprovers';
import BypassDocuments from './system-failure/BypassDocument';
import BackupDocument from './system-failure/BackupDocument';
import TestingDocument from './system-failure/TestingDocument';
import AuditDocument from './system-failure/AuditDocument';
import { TEVVService } from 'services/TEVVService';
import Feedback from './components/FeedBack';
import useBroadCast from 'shared/hooks/useBroadCast';
import CommentsSection from './components/CommentsSection';
import AuditTrail from 'modules/ai-inventory/datasets/records-dataset/components/AuditTrail';
import AgenticToolsSBOM from './components/AgenticToolsSBOM';
import { getChipColorByIndex } from 'shared/helpers/RandomChipColor';
import ApprovalPanel from './components/ApprovalPanel';
import AIAuditTrail from './components/AIAuditTrail';
import SendAssessment from '../../../risk-management/external-assessments/components/SendAssessment';
import dayjs from 'dayjs';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';
import useSize from 'shared/hooks/useSize';

const dateFns = new DateFnsAdapter();

export interface FileDetails {
  uploadedBy: string;
  originalFilename: string;
  fileId: string;
  fileUrl: string;
  status: string;
  _id: string;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentProps {
  basecategory: string;
  stage: string;
  category: string;
  type: string;
  fileDetails: FileDetails;
  templateId: string;
}

export interface FileDetail {
  uploadedBy: string;
  originalFilename: string;
  fileId: string;
  fileUrl: string;
  status: string;
  _id: string;
  createdAt: string;
  updatedAt: string;
}
export interface TemplateProps {
  _id: string;
  basecategory: string;
  stage: string;
  aisystemId: string;
  category: string;
  type: string;
  status: string;
  description: string;
  isRequired: boolean;
  organizationId: string;
  templateId: string;
  fileDetails: FileDetail[];
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const getStatusColors = (status: string): any => {
  switch (status) {
    case 'Pending':
      return {
        icon: (
          <QueryBuilderIcon
            sx={{
              color: '#1e40af',
              width: '65px',
              height: '65px',
            }}
          />
        ),
        color: '#dbeafe',
      };
    case 'Draft':
      return {
        icon: (
          <FileCopyIcon
            sx={{
              color: '',
              width: '65px',
              height: '65px',
            }}
          />
        ),
        color: '#f3f4f6',
      };
    case 'Archived':
      return {
        icon: (
          <ClassIcon
            sx={{
              color: '#92400e',
              width: '65px',
              height: '65px',
            }}
          />
        ),
        color: '#fef3c7',
      };
    case 'Decommissioned':
      return {
        icon: (
          <NotInterestedIcon
            sx={{
              color: '#991b1b',
              width: '65px',
              height: '65px',
            }}
          />
        ),
        color: '#fee2e2',
      };
    case 'Approved':
      return {
        icon: (
          <Box
            sx={{
              background: '#DCFCE7',
              width: '4.5rem',
              height: '4.5rem',
              alignContent: 'center',
              borderRadius: '100%',
            }}
            mb={2}
          >
            <ApprovedCheck />
          </Box>
        ),
        color: '#fef3c7',
      };
    default:
      return {
        icon: (
          <FileCopyIcon
            sx={{
              color: '',
              width: '65px',
              height: '65px',
            }}
          />
        ),
        color: '#f3f4f6',
      };
  }
};

const ViewFormikContext = (): any => {
  const { values, setValues } = useFormikContext<any>();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.log(location.state);
    if (location?.state?.from) {
      setValues({
        ...values,
        status: [false, false, true],
      });
    }
    if (location?.state?.isFromDataset) {
      setValues({
        ...values,
        status: [false, true, false],
      });
    }
    navigate(location.pathname, { replace: true, state: null });
  }, []);

  return null;
};

const RecordSolutionsDetail = (): ReactElement => {
  const { isMobile, isTablet } = useSize();
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const { aiSystemId } = useParams();
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );

  const navigate = useNavigate();
  const [systemDetail, setSystemDetail] = useState<IAISystem>();
  const [openDeleteAISystem, setOpenDeleteAISystem] = React.useState(false);
  const [technicalFieldsToShow] = useState(TechnicalFields);
  const location = useLocation();
  const [statusMenus, setStatusMenus] = useState<string[]>([]);
  const [editTierLevel, setEditTierLevel] = useState<any>(false);
  const internal = useMemo(() => {
    if (location.state) {
      const { internal: _internal }: any = location.state;
      return _internal;
    }
    return false;
  }, [location.state]);
  const [showRunAssessment, setShowRunAssessment] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [userTier, setUserTier] = useState<string[]>([]);
  const [documentList, setDocumentList] = useState<DocumentProps[]>([]);
  const [templateList, setTemplateList] = useState<TemplateProps[]>([]);
  const [showSendAssessment, setShowSendAssessment] = useState(false);
  const open = Boolean(anchorEl);
  const handleClick = (event: any): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (): void => {
    setAnchorEl(null);
  };

  // API Calls
  const loadUserTier = (): void => {
    TierApproverService.getUserTier()
      .then((response) => {
        console.log(response);
        const { data } = response.data;
        setUserTier(data);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };
  const getDocumentList = (): any => {
    if (aiSystemId)
      TEVVService.getFiles(aiSystemId)
        .then((res) => {
          const { data } = res.data;
          setDocumentList(data);
        })
        .catch((error) => {
          Utils.handleErrorResponse(error);
        });
  };

  const getTemplateList = (): void => {
    if (aiSystemId) {
      TEVVService.getFileDetail(aiSystemId)
        .then((res) => {
          const { data } = res.data;
          setTemplateList(data);
        })
        .catch((error) => {
          Utils.handleErrorResponse(error);
        });
    }
  };

  const loadDetails = (): void => {
    setIsLoadingDetail(typeof systemDetail !== 'object');
    AISystemsService.getAISystemDetail(`${aiSystemId}`)
      .then((response) => {
        const { data } = response.data;
        setSystemDetail(data);
      })
      .catch(() => {
        navigate('/ai-systems');
      })
      .finally(() => {
        setIsLoadingDetail(false);
      });
  };

  const updateRiskTier = (data: any): void => {
    AISystemsService.updateAISystems(aiSystemId, {
      ...systemDetail,
      riskTier: data,
    })
      .then(() => {
        loadDetails();
      })
      .catch((e) => {
        Toast.showErrorMessage(e);
      });
  };

  // UI Actions
  const handleCloseDeleteAISystem = (): void => {
    setOpenDeleteAISystem(false);
  };

  const handleDeleteAISystem = (id: any): void => {
    AISystemsService.deleteAISystems(id)
      .then(() => {
        Toast.showSuccessMessage(`Successfully delete datasets`);
        setOpenDeleteAISystem(false);
        navigate(-1);
      })
      .catch((e) => {
        Toast.showErrorMessage(e);
      });
  };

  const actionOnChangeStatus = (status: string): void => {
    handleClose();
    AISystemsService.approveAISystem({}, `${systemDetail?._id}`, status)
      .then(() => {
        loadDetails();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const downloadFiles = (templateId: string, fileId: string): void => {
    TEVVService.downLoadFile(templateId, fileId)
      .then((res) => {
        Utils.handleSuccessResponse(res);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  // Hooks
  useEffect(() => {
    loadUserTier();
    getDocumentList();
    getTemplateList();
  }, []);

  useEffect(() => {
    if (aiSystemId) {
      loadDetails();
    }
  }, [aiSystemId]);

  useEffect(() => {
    if (systemDetail) {
      const tMenus = ['Pending', 'Draft', 'Archived', 'Decommissioned'];
      if (userTier.includes(systemDetail.riskTier)) {
        tMenus.splice(0, 0, 'Approved');
      }
      const oldStatusIndex = tMenus.findIndex(
        (s) => systemDetail.approvalStatus === s
      );
      if (oldStatusIndex > -1) {
        tMenus.splice(oldStatusIndex, 1);
      }
      setStatusMenus(tMenus);
    }
  }, [systemDetail, userTier]);

  useBroadCast({
    eventName: BROADCAST_MESSAGES.RELOAD_AI_DOCUMENT_LIST,
    onBroadCast: () => {
      getDocumentList();
    },
  });

  // const onUploadDocument = (): void => {};

  // UI Elements
  return (
    <Box className="ai-inventory ai-solutions">
      <HeadTag title="AI Systems" />
      {isLoadingDetail && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoadingDetail && systemDetail && (
        <Box className="ai-solutions record-details mt-4">
          <Stack
            spacing={2}
            direction={isMobile ? 'column' : 'row'}
            justifyContent="space-between"
          >
            <AppPrimaryButton
              title=""
              startIcon={
                <SvgIcon
                  component={Back}
                  inheritViewBox
                  className="view-icon"
                  fontSize="inherit"
                  htmlColor="white"
                />
              }
              onClick={() => {
                navigate('/ai-systems', {
                  state: {
                    isPublic: systemDetail?.publicSubmissionMail !== null,
                  },
                });
              }}
            />
            {systemDetail.isConverted && (
              <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
                {orgACLConfig.aiInventory?.includes(
                  'aiInventory.aiSystem.aiInventory.view.editSolution'
                ) && (
                  <AppPrimaryButton
                    title="Edit"
                    startIcon={
                      <SvgIcon
                        component={Edit}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        htmlColor="white"
                      />
                    }
                    onClick={() => {
                      navigate(`/ai-systems/edit/${aiSystemId}`, {
                        state: {
                          internal,
                          systemDetail,
                        },
                      });
                    }}
                  />
                )}
                {orgACLConfig.aiInventory?.includes(
                  'aiInventory.aiSystem.aiInventory.view.deleteSolution'
                ) && (
                  <AppPrimaryButton
                    title="Delete"
                    className="btn-danger"
                    startIcon={
                      <SvgIcon
                        component={Trash}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        htmlColor="white"
                      />
                    }
                    onClick={() => {
                      setOpenDeleteAISystem(true);
                    }}
                  />
                )}
                {orgACLConfig.aiInventory?.includes(
                  'aiInventory.aiSystem.aiInventory.view.runRiskAssessment'
                ) && (
                  <AppPrimaryButtonOutlined
                    title="Run Risk Assessment"
                    startIcon={<PlayCircleOutlineIcon />}
                    onClick={() => {
                      setShowRunAssessment(true);
                    }}
                  />
                )}
                {orgACLConfig.aiInventory?.includes(
                  'aiInventory.aiSystem.aiInventory.view.createRisk'
                ) && (
                  <AppPrimaryButton
                    title="Create Risk"
                    startIcon={
                      <SvgIcon
                        component={Plus}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        htmlColor="white"
                      />
                    }
                    onClick={() => {
                      navigate('/risk-register/add');
                    }}
                  />
                )}
              </Stack>
            )}
          </Stack>
          <Formik
            initialValues={{
              status: [true, false],
              tevvStatus: [true, false, false],
              aiSystemStatus: [true, false],
            }}
            onSubmit={() => console.log('test')}
          >
            {({ values, setValues }) => (
              <>
                <ViewFormikContext />
                <Stack direction="column" spacing={2}>
                  <Stack direction="row">
                    <Box className="solutions-title mt-5">
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '24px !important',
                          fontWeight: '700 !important',
                        }}
                      >
                        {systemDetail.solutionName}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: '14px !important',
                        }}
                      >
                        AI System Details
                      </Typography>
                    </Box>
                  </Stack>
                  {systemDetail.isConverted && (
                    <Box className="solutions-menu">
                      <AppFormikButtonTiles
                        variant="outlined"
                        name="status"
                        size="small"
                        label=""
                        disabled={false}
                        multichoice={false}
                        className="app-group-button"
                        buttons={[
                          {
                            value: 'Details',
                            tooltip:
                              'Overall AI system metadata, ownership, users, and general configurations for this AI system.',
                          },
                          {
                            value: 'Datasets',
                            tooltip: 'Linked Datasets to the AI system.',
                          },
                          {
                            value: 'Risk Assessment',
                            tooltip:
                              'Evaluate compliance risks, impact levels, and governance hazard classifications for the system.',
                          },
                          {
                            value: 'SBOM',
                            tooltip:
                              'Review the Software Bill of Materials detailing model components, packages, dependencies, and vulnerabilities.',
                          },
                          {
                            value: 'TEVV',
                            tooltip:
                              'Monitor Test, Evaluation, Verification, and Validation reports, ensuring system reliability and quality metrics.',
                          },
                          {
                            value: 'System Failure',
                            tooltip:
                              'Track reported incidents, operational anomalies, outages, and system-specific failure logs.',
                          },
                          {
                            value: 'Feedback',
                            tooltip:
                              'Review end-user responses, qualitative evaluations, and reported output performance flags.',
                          },
                          {
                            value: 'Approval',
                            tooltip:
                              'Check governance sign-offs, deployment review status, and compliance workflows required for release.',
                          },
                          {
                            value: 'Logs',
                            tooltip:
                              'Access raw execution records, transaction events, and real-time operational activity traces.',
                          },
                          {
                            value: 'Comments',
                            tooltip:
                              'Collaborate with team members, record review notes, and track internal discussions.',
                          },
                        ]}
                        outerLabel
                      />
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column-reverse', md: 'row' },
                      gap: { xs: 2, md: '1rem' },
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        flex: { md: '1 1 auto' },
                        minWidth: 0,
                        marginTop: isTablet ? '15px' : 0,
                      }}
                    >
                      {values.status[0] && (
                        <Box className="record-container">
                          <Box className="record-container-title">
                            <Typography variant="h4">
                              Solution Details
                            </Typography>
                          </Box>
                          <Box className="container-wrapper">
                            <Stack direction="column" spacing={3}>
                              <Box className="bussiness-content">
                                <Typography variant="h5" className="mb-5">
                                  Business & Usage Information
                                </Typography>
                                <Grid
                                  container
                                  spacing={{ xs: 2, sm: 3, md: 5 }}
                                >
                                  <Grid xs={12} sm={6} item>
                                    <Box className="content-detail">
                                      <Typography variant="body2">
                                        Solution Name
                                      </Typography>
                                      <Typography variant="body1">
                                        {systemDetail.solutionName}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid xs={12} sm={6} item>
                                    <Box className="content-detail">
                                      <Typography variant="body2">
                                        Organisation Unit
                                      </Typography>
                                      <Typography variant="body1">
                                        {systemDetail.organizationUnit}
                                      </Typography>
                                    </Box>
                                  </Grid>

                                  <Grid xs={12} sm={6} item>
                                    <Box className="content-detail">
                                      <Typography variant="body2">
                                        Solution Owner
                                      </Typography>
                                      <Typography variant="body1">
                                        {systemDetail.solutionOwner}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid xs={12} sm={6} item>
                                    <Box className="content-detail">
                                      <Typography variant="body2">
                                        Organisation Sub Unit
                                      </Typography>
                                      <Typography variant="body1">
                                        {systemDetail.organizationSubUnit}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid xs={12} sm={6} item>
                                    <Box className="content-detail">
                                      <Typography variant="body2">
                                        Stakeholders
                                      </Typography>
                                      <Typography variant="body1">
                                        {systemDetail.stakeholder}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid xs={12} sm={6} item>
                                    <Box className="content-detail">
                                      <Typography variant="body2">
                                        Legal Entity
                                      </Typography>
                                      <Typography variant="body1">
                                        {systemDetail.legalEntity}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid xs={12} sm={6} item>
                                    <Box className="content-detail">
                                      <Typography variant="body2">
                                        User/Customer
                                      </Typography>
                                      <Typography variant="body1">
                                        {systemDetail.userCustomerName}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid xs={12} sm={6} item>
                                    <Box className="content-detail">
                                      <Typography variant="body2">
                                        Bussiness Processes
                                      </Typography>
                                      <Typography variant="body1">
                                        {systemDetail.businessProcesses}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid xs={12} sm={6} item>
                                    <Box className="content-detail">
                                      <Typography variant="body2">
                                        Uses
                                      </Typography>
                                      <Typography variant="body1">
                                        {systemDetail.uses}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid xs={12} item>
                                    <Box className="content-detail">
                                      <Typography variant="body2">
                                        Purpose
                                      </Typography>
                                      <Typography variant="body1">
                                        {systemDetail.purpose}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid xs={12} item>
                                    <Box className="content-detail">
                                      <Typography variant="body2">
                                        Bussiness Goals
                                      </Typography>
                                      <Typography variant="body1">
                                        {systemDetail.businessGoals}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid xs={12} item>
                                    <Box className="content-detail">
                                      <Typography variant="body2">
                                        Bussiness Value
                                      </Typography>
                                      <Typography variant="body1">
                                        {systemDetail.businessValue}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                              <Box className="technical-content">
                                <Typography variant="h5" className="mb-5">
                                  Technical & Governance Details
                                </Typography>
                                <Grid
                                  spacing={{ xs: 2, sm: 3, md: 5 }}
                                  container
                                >
                                  {technicalFieldsToShow.includes(
                                    'platform'
                                  ) && (
                                    <Grid xs={12} sm={6} item>
                                      <Box className="content-detail">
                                        <Typography variant="body2">
                                          Platform
                                        </Typography>
                                        <Typography variant="body1">
                                          {systemDetail.platformName}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {technicalFieldsToShow.includes(
                                    'riskTier'
                                  ) && (
                                    <Grid xs={12} sm={6} item>
                                      <Box className="content-detail">
                                        <Typography variant="body2">
                                          Risk tier
                                        </Typography>
                                        <Typography variant="body1">
                                          {systemDetail.riskTier}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {technicalFieldsToShow.includes(
                                    'opensource'
                                  ) && (
                                    <Grid xs={12} sm={6} item>
                                      <Box className="content-detail">
                                        <Typography variant="body2">
                                          Open Source
                                        </Typography>
                                        <Typography variant="body1">
                                          {systemDetail.openSource
                                            ? 'Yes'
                                            : 'No'}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  <Grid xs={12} sm={6} item>
                                    <Box className="content-detail">
                                      <Typography variant="body2">
                                        Source Type
                                      </Typography>
                                      <Typography variant="body1">
                                        {systemDetail.sourceType}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  {technicalFieldsToShow.includes(
                                    'aimodeltypes'
                                  ) && (
                                    <Grid xs={12} sm={6} item>
                                      <Box className="content-detail">
                                        <Typography variant="body2">
                                          AI Model Types
                                        </Typography>
                                        <ul>
                                          {systemDetail.aiModelTypes.map(
                                            (type) => (
                                              <li key={type}>
                                                <Typography variant="body1">
                                                  {type}
                                                </Typography>
                                              </li>
                                            )
                                          )}
                                        </ul>
                                      </Box>
                                    </Grid>
                                  )}
                                  {technicalFieldsToShow.includes(
                                    'technicaldocument'
                                  ) && (
                                    <Grid xs={12} sm={6} item>
                                      <Box className="content-detail">
                                        <Typography variant="body2">
                                          Technical documentation link
                                        </Typography>
                                        <Typography variant="body1">
                                          {
                                            systemDetail.technicalDocumentationLink
                                          }
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {technicalFieldsToShow.includes(
                                    'vendorname'
                                  ) && (
                                    <Grid xs={12} sm={6} item>
                                      <Box className="content-detail">
                                        <Typography variant="body2">
                                          Vendor name
                                        </Typography>
                                        <Typography variant="body1">
                                          {systemDetail.vendorName}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {technicalFieldsToShow.includes(
                                    'consumtionmethod'
                                  ) && (
                                    <Grid xs={12} sm={6} item>
                                      <Box className="content-detail">
                                        <Typography variant="body2">
                                          Consumption method
                                        </Typography>
                                        <Typography variant="body1">
                                          {systemDetail.consumptionMethod}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {technicalFieldsToShow.includes(
                                    'environment'
                                  ) && (
                                    <Grid xs={12} sm={6} item>
                                      <Box className="content-detail">
                                        <Typography variant="body2">
                                          Environment
                                        </Typography>
                                        <Typography variant="body1">
                                          {systemDetail.environment}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {technicalFieldsToShow.includes(
                                    'watermarkingtechnique'
                                  ) && (
                                    <Grid xs={12} sm={6} item>
                                      <Box className="content-detail">
                                        <Typography variant="body2">
                                          Watermarking technique
                                        </Typography>
                                        <Typography variant="body1">
                                          {systemDetail.watermarkingTechnique}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {technicalFieldsToShow.includes(
                                    'lifecyclestage'
                                  ) && (
                                    <Grid xs={12} sm={6} item>
                                      <Box className="content-detail">
                                        <Typography variant="body2">
                                          Lifecycle stage
                                        </Typography>
                                        <Typography variant="body1">
                                          {systemDetail.lifecycleStage}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {technicalFieldsToShow.includes(
                                    'versioncontrol'
                                  ) && (
                                    <Grid xs={12} sm={6} item>
                                      <Box className="content-detail">
                                        <Typography variant="body2">
                                          Version control method
                                        </Typography>
                                        <Typography variant="body1">
                                          {systemDetail.versionControlMethod}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {technicalFieldsToShow.includes(
                                    'selectcapability'
                                  ) && (
                                    <Grid xs={12} sm={6} item>
                                      <Box className="content-detail">
                                        <Typography variant="body2">
                                          Decision capability
                                        </Typography>
                                        <Typography variant="body1">
                                          {systemDetail.decisionCapability}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {technicalFieldsToShow.includes(
                                    'builddate'
                                  ) && (
                                    <Grid xs={12} sm={6} item>
                                      <Box className="content-detail">
                                        <Typography variant="body2">
                                          Build date
                                        </Typography>
                                        <Typography variant="body1">
                                          {systemDetail.buildDate
                                            ? dateFns
                                                .date(systemDetail.buildDate)
                                                .toDateString()
                                            : ''}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {technicalFieldsToShow.includes(
                                    'trainingdate'
                                  ) && (
                                    <Grid xs={12} sm={6} item>
                                      <Box className="content-detail">
                                        <Typography variant="body2">
                                          Last training date
                                        </Typography>
                                        <Typography variant="body1">
                                          {systemDetail.lastTrainingDate
                                            ? dateFns
                                                .date(
                                                  systemDetail.lastTrainingDate
                                                )
                                                .toDateString()
                                            : ''}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            </Stack>
                          </Box>
                        </Box>
                      )}
                      {values.status[1] && (
                        <LinkedDatasets
                          aiSystemId={`${aiSystemId}`}
                          datasets={systemDetail.datasetId}
                        />
                      )}
                      {values.status[2] && (
                        <Stack spacing={5}>
                          <LinkedAssessments
                            type="AI"
                            aiSystemId={`${aiSystemId}`}
                            onClickSendAssessment={() => {
                              setShowSendAssessment(true);
                            }}
                            showSendAssessment
                          />
                          <LinkedRiskRegisters
                            type="AI"
                            aiSystemId={`${aiSystemId}`}
                            riskRegisters={systemDetail.riskRegisters}
                            reloadDetail={loadDetails}
                          />
                        </Stack>
                      )}
                      {values.status[4] && (
                        <Box className="record-container">
                          <Box className="record-container-title">
                            <Typography variant="h4">
                              Test, Evaluation, Verification & Validation
                              <br />
                              <span
                                style={{
                                  fontSize: '16px',
                                  color: 'white',
                                  fontWeight: 'normal',
                                }}
                              >
                                {' '}
                                Manage TEVV documents for
                              </span>
                            </Typography>
                          </Box>
                          <Box className="solutions-menu">
                            <AppFormikButtonTiles
                              variant="outlined"
                              name="tevvStatus"
                              size="small"
                              label=""
                              disabled={false}
                              multichoice={false}
                              className="app-group-button"
                              buttons={[
                                {
                                  value: 'Documents',
                                },
                                {
                                  value: 'Compliance Status',
                                },
                                {
                                  value: 'Audit Trail',
                                },
                              ]}
                              outerLabel
                            />
                          </Box>
                          {values.tevvStatus[0] && (
                            <Documents
                              documentList={documentList.filter((doc) =>
                                doc.basecategory.includes('tevv')
                              )}
                              getDocumentList={getDocumentList}
                              templateList={templateList.filter((template) =>
                                template.basecategory.includes('tevv')
                              )}
                              downloadFile={(
                                templateId: string,
                                fileId: string
                              ) => {
                                downloadFiles(templateId, fileId);
                              }}
                            />
                          )}
                          {values.tevvStatus[1] && (
                            <Box className="tevv-complience-wrapper">
                              <Complience />
                            </Box>
                          )}
                          {values.tevvStatus[2] && (
                            <AIAuditTrail
                              title="TEVV Audit Trail"
                              entityType={EntityType.AI_SYSTEM_TEVV}
                            />
                          )}
                        </Box>
                      )}
                      {values.status[5] && (
                        <Box className="record-container">
                          <Box className="record-container-title">
                            <Typography variant="h4">
                              System Failure Management <br />
                              <span
                                style={{
                                  fontSize: '16px',
                                  color: 'white',
                                  fontWeight: 'normal',
                                }}
                              >
                                Manage failure scenarios,backup and redundancy
                                documentation for
                              </span>
                            </Typography>
                          </Box>
                          <Box className="solutions-menu">
                            <AppFormikButtonTiles
                              variant="outlined"
                              name="aiSystemStatus"
                              size="small"
                              label=""
                              disabled={false}
                              multichoice={false}
                              className="app-group-button"
                              buttons={[
                                {
                                  value: 'Bypass and Contingency',
                                },
                                {
                                  value: 'Backup and Redundancy',
                                },
                                {
                                  value: 'Testing and Simulation',
                                },
                                {
                                  value: 'Audit Trail',
                                },
                              ]}
                              outerLabel
                            />
                          </Box>

                          {values.aiSystemStatus[0] && (
                            <BypassDocuments
                              documentList={documentList.filter(
                                (doc) =>
                                  doc.basecategory.includes('system-failure') &&
                                  doc.stage.includes('Bypass & Contingency')
                              )}
                              getDocumentList={getDocumentList}
                              templateList={templateList.filter(
                                (template) =>
                                  template.basecategory.includes(
                                    'system-failure'
                                  ) &&
                                  template.stage.includes(
                                    'Bypass & Contingency'
                                  )
                              )}
                              downloadFile={(
                                templateId: string,
                                fileId: string
                              ) => {
                                downloadFiles(templateId, fileId);
                              }}
                            />
                          )}
                          {values.aiSystemStatus[1] && (
                            <BackupDocument
                              documentList={documentList.filter(
                                (doc) =>
                                  doc.basecategory.includes('system-failure') &&
                                  doc.stage.includes('Backup & Redundancy')
                              )}
                              getDocumentList={getDocumentList}
                              templateList={templateList.filter(
                                (template) =>
                                  template.basecategory.includes(
                                    'system-failure'
                                  ) &&
                                  template.stage.includes('Backup & Redundancy')
                              )}
                              downloadFile={(
                                templateId: string,
                                fileId: string
                              ) => {
                                downloadFiles(templateId, fileId);
                              }}
                            />
                          )}
                          {values.aiSystemStatus[2] && (
                            <TestingDocument
                              documentList={documentList.filter(
                                (doc) =>
                                  doc.basecategory.includes('system-failure') &&
                                  doc.stage.includes('Testing & Simulation')
                              )}
                              getDocumentList={getDocumentList}
                              templateList={templateList.filter(
                                (template) =>
                                  template.basecategory.includes(
                                    'system-failure'
                                  ) &&
                                  template.stage.includes(
                                    'Testing & Simulation'
                                  )
                              )}
                              downloadFile={(
                                templateId: string,
                                fileId: string
                              ) => {
                                downloadFiles(templateId, fileId);
                              }}
                            />
                          )}
                          {values.aiSystemStatus[3] && (
                            <>
                              <AIAuditTrail
                                entityType={EntityType.AI_SYSTEM_FAILURE}
                              />
                              {false && <AuditDocument />}
                            </>
                          )}
                        </Box>
                      )}
                      {values.status[6] && <Feedback />}
                      {values.status[7] && <ApprovalPanel />}
                      {/* {values.status[3] && <AgenticToolsSBOM />} */}
                      {values.status[3] && <AgenticToolsSBOM />}
                      {/* {values.status[8] && <ComingSoonCard title="History" />} */}
                      {values.status[8] && (
                        <AuditTrail
                          title="History"
                          entityType={EntityType.AI_SYSTEM}
                        />
                      )}
                      {values.status[9] && (
                        <CommentsSection entityType={EntityType.AI_SYSTEM} />
                      )}
                    </Box>

                    {values.status[0] && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: isTablet ? 'row' : 'column',
                          alignItems: isTablet ? 'stretch' : 'flex-start',
                          gap: isTablet ? '1rem' : 0,
                        }}
                      >
                        {/* NEW APPROVAL STATUS UI */}
                        <Box
                          sx={{
                            padding: '10px',
                            background: 'white',
                            borderRadius: '10px',
                            minWidth: '17.5rem',
                            height: isTablet ? 'auto' : 'max-content',
                            flex: isTablet ? 1 : 'unset',
                            display: isTablet ? 'flex' : 'block',
                            flexDirection: 'column',
                          }}
                        >
                          {systemDetail.isConverted && (
                            <Stack
                              direction="column"
                              spacing={1}
                              sx={{ flex: isTablet ? 1 : 'unset' }}
                            >
                              <Stack spacing={2}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontSize: '24px !important',
                                    fontWeight: '700 !important',
                                  }}
                                >
                                  Approval Status
                                </Typography>
                                <Typography
                                  variant="body1"
                                  className="text-gray"
                                >
                                  Current approval state of this solutions
                                </Typography>
                              </Stack>

                              <Box
                                sx={{ textAlign: 'center' }}
                                className="my-5"
                              >
                                <Box sx={{ justifyItems: 'center' }}>
                                  {
                                    getStatusColors(systemDetail.approvalStatus)
                                      ?.icon
                                  }
                                </Box>

                                <Typography className="f20 fw-7">
                                  {systemDetail.approvalStatus}
                                </Typography>

                                {systemDetail.approvalStatus === 'Approved' && (
                                  <Typography
                                    variant="body1"
                                    className="text-gray"
                                    mt={2}
                                    fontWeight={600}
                                  >
                                    Approved on{' '}
                                    {dayjs(
                                      systemDetail?.updatedAt ?? dayjs()
                                    ).format('DD/MM/YYYY')}
                                  </Typography>
                                )}
                              </Box>
                              {orgACLConfig.aiInventory?.includes(
                                'aiInventory.aiSystem.aiInventory.view.details.changeApprovalStatus'
                              ) && (
                                <Box
                                  onClick={handleClick}
                                  className="solutions-approval-status cursor"
                                  sx={{
                                    cursor: 'pointer',
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={2}
                                    sx={{
                                      alignItems: 'center',
                                      border: '1px solid #f3f4f6',
                                      borderRadius: '4px',
                                      padding: '8px 10px',
                                      justifyContent: 'space-between',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: '14px !important',
                                      }}
                                    >
                                      Change approval status
                                    </Typography>

                                    <KeyboardArrowDownIcon />
                                  </Stack>
                                </Box>
                              )}
                              <Menu
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                              >
                                {statusMenus.map((status) => (
                                  <MenuItem
                                    key={status}
                                    onClick={() => actionOnChangeStatus(status)}
                                  >
                                    {status}
                                  </MenuItem>
                                ))}
                              </Menu>
                            </Stack>
                          )}
                        </Box>
                        {/* NEW APPROVAL RISK TIER */}
                        <Box
                          sx={{
                            padding: '10px',
                            background: 'white',
                            borderRadius: '10px',
                            minWidth: '17.5rem',
                            height: isTablet ? 'auto' : 'max-content',
                            flex: isTablet ? 1 : 'unset',
                            display: isTablet ? 'flex' : 'block',
                            flexDirection: 'column',
                            mt: isTablet ? 0 : 5,
                          }}
                        >
                          {systemDetail.isConverted && (
                            <Stack
                              direction="column"
                              spacing={1}
                              sx={{ flex: isTablet ? 1 : 'unset' }}
                            >
                              <Box
                                className="solutions-status"
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  height: isTablet ? '100%' : 'auto',
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontSize: '24px !important',
                                    fontWeight: '700 !important',
                                  }}
                                >
                                  Risk Tier
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: '0.5rem',
                                    alignItems: 'center',
                                  }}
                                  mt={2}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '14px !important',
                                      color: '#7E7E7E',
                                    }}
                                  >
                                    Assigned tier level
                                  </Typography>

                                  {orgACLConfig.aiInventory?.includes(
                                    'aiInventory.aiSystem.aiInventory.view.details.riskTier.editTierLevel'
                                  ) && (
                                    <DriveFileRenameOutlineIcon
                                      fontSize="small"
                                      sx={{
                                        color: '#7E7E7E',
                                        cursor: 'pointer',
                                      }}
                                      onClick={() => {
                                        setEditTierLevel((prev: any) => !prev);
                                      }}
                                    />
                                  )}
                                </Box>
                                {!editTierLevel ? (
                                  ['Low', 'Moderate', 'High', 'Critical'].map(
                                    (data: any, index: number) => (
                                      <Chip
                                        label={data}
                                        sx={() => {
                                          const { bg, text } =
                                            getChipColorByIndex(index);
                                          return {
                                            background: bg,
                                            color: text,
                                            display:
                                              systemDetail?.riskTier === data
                                                ? ''
                                                : 'none',
                                            mt: 2,
                                          };
                                        }}
                                        size="small"
                                      />
                                    )
                                  )
                                ) : (
                                  <Formik
                                    initialValues={{
                                      assginedTierLevel: systemDetail?.riskTier,
                                    }}
                                    onSubmit={() => {}}
                                  >
                                    {({ values }) => (
                                      <AppFormikSelect
                                        name="assginedTierLevel"
                                        className="rework-selection"
                                        notched={false}
                                        labelId="fix-selection-label"
                                        testid="solicitor-dropdown"
                                        MenuProps={{
                                          className:
                                            'menu-fix-selection single-fix-rework',
                                        }}
                                        outerLabel
                                        placeholder="Select Level"
                                        notEmphasized
                                        notSelectablePlaceholder
                                        label=""
                                        items={[
                                          'Low',
                                          'Moderate',
                                          'High',
                                          'Critical',
                                        ]}
                                        onChange={() =>
                                          updateRiskTier(
                                            values.assginedTierLevel
                                          )
                                        }
                                      />
                                    )}
                                  </Formik>
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '14px !important',
                                    color: '#7E7E7E',
                                  }}
                                  mt={3}
                                >
                                  No risk assessments
                                </Typography>
                                <Box mt={3}>
                                  {orgACLConfig.aiInventory?.includes(
                                    'aiInventory.aiSystem.aiInventory.view.details.riskTier.runAssessment'
                                  ) && (
                                    <AppPrimaryButtonOutlined
                                      variant="outlined"
                                      title="Run Assessment"
                                      onClick={() => {
                                        setValues({
                                          ...values,
                                          status: [false, false, true],
                                        });
                                      }}
                                      className="btn-small"
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Stack>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Stack>
              </>
            )}
          </Formik>
        </Box>
      )}

      <ConfirmDialog
        title="Delete this AI system?"
        subTitle={
          <>
            <b>{systemDetail?.solutionName}</b> will be permanently removed from
            your inventory, along with its setup progress. This can't be undone.
          </>
        }
        showConfirm={openDeleteAISystem}
        cancelTitle="Close"
        successTitle="Delete"
        onSuccess={() => {
          handleCloseDeleteAISystem();
          handleDeleteAISystem(aiSystemId);
        }}
        onCancel={handleCloseDeleteAISystem}
        onCloseDialog={handleCloseDeleteAISystem}
      />

      {showSendAssessment && systemDetail && (
        <SendAssessment
          assessmentFor={aiSystemId}
          type="AI"
          open={showSendAssessment}
          onCloseAssessment={(reload: boolean) => {
            setShowSendAssessment(false);
            if (reload) {
              window.postMessage(BROADCAST_MESSAGES.RELOAD_ASSESSMENTS);
            }
          }}
          entityTile={systemDetail.solutionName}
        />
      )}

      {showRunAssessment && (
        <QuickRunAssessment
          assessmentFor={aiSystemId}
          type="AI"
          open={showRunAssessment}
          onCloseAssessment={(reload: boolean) => {
            if (reload) {
              window.postMessage(BROADCAST_MESSAGES.RELOAD_ASSESSMENTS);
            }
            setShowRunAssessment(false);
          }}
        />
      )}
    </Box>
  );
};

export default RecordSolutionsDetail;
